/**
 * callScriptDraftDB.js — IndexedDB wrapper for call script draft storage
 *
 * Stores the last 5 call script form drafts locally so users can recover
 * unsaved work (e.g., when the dialog closes without a lead_id, browser crash, etc.).
 *
 * Each draft is keyed by a unique draftId = `${practiceId}_${scriptCategory}_${timestamp}`.
 * Drafts auto-expire after 24 hours and are cleared on logout.
 */

const DB_NAME = 'DentalSEM_CallScriptDrafts';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
const MAX_DRAFTS = 5;
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Open (or create) the IndexedDB database
 */
function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'draftId' });
                store.createIndex('practiceId', 'practiceId', { unique: false });
                store.createIndex('scriptCategory', 'scriptCategory', { unique: false });
                store.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Save or update a draft in IndexedDB
 *
 * @param {Object} draft
 * @param {string} draft.draftId - Unique ID for this draft session
 * @param {number} draft.practiceId - Practice ID
 * @param {string} draft.scriptCategory - 'INBOUND' | 'OUTBOUND' | 'TCO'
 * @param {string} draft.scriptName - Display name of the script
 * @param {number|null} draft.scriptId - Script form ID (if available)
 * @param {string} draft.callerName - Name entered for the caller
 * @param {number|null} draft.leadId - Lead ID (may be null for orphaned drafts)
 * @param {Object} draft.formData - The full formData state object
 * @param {Array} draft.cardNotes - Card notes array
 * @param {string} draft.callNotes - Call notes string
 * @param {number} draft.currentCardIndex - Current position in the script
 * @param {number} draft.updatedAt - Timestamp of last update
 */
export async function saveDraft(draft) {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        // Add/update the draft
        draft.updatedAt = Date.now();
        store.put(draft);

        await new Promise((resolve, reject) => {
            tx.oncomplete = resolve;
            tx.onerror = () => reject(tx.error);
        });

        // Enforce MAX_DRAFTS limit — remove oldest drafts beyond the cap
        await enforceLimit(draft.practiceId);

        db.close();
        return true;
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to save draft', err);
        return false;
    }
}

/**
 * Get all drafts for a practice, optionally filtered by scriptCategory
 * Returns drafts sorted newest-first, excluding expired ones
 *
 * @param {number} practiceId
 * @param {string} [scriptCategory] - Optional filter
 * @returns {Promise<Array>}
 */
export async function getDrafts(practiceId, scriptCategory) {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const index = store.index('practiceId');

        const results = await new Promise((resolve, reject) => {
            const request = index.getAll(practiceId);
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });

        db.close();

        const now = Date.now();
        return results
            .filter((d) => {
                // Exclude expired drafts
                if (now - d.updatedAt > EXPIRY_MS) return false;
                // Filter by category if provided
                if (scriptCategory && d.scriptCategory !== scriptCategory) return false;
                return true;
            })
            .sort((a, b) => b.updatedAt - a.updatedAt);
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to get drafts', err);
        return [];
    }
}

/**
 * Get a single draft by its draftId
 *
 * @param {string} draftId
 * @returns {Promise<Object|null>}
 */
export async function getDraft(draftId) {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const result = await new Promise((resolve, reject) => {
            const request = store.get(draftId);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });

        db.close();
        return result;
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to get draft', err);
        return null;
    }
}

/**
 * Delete a specific draft
 *
 * @param {string} draftId
 */
export async function deleteDraft(draftId) {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.delete(draftId);

        await new Promise((resolve, reject) => {
            tx.oncomplete = resolve;
            tx.onerror = () => reject(tx.error);
        });

        db.close();
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to delete draft', err);
    }
}

/**
 * Clear ALL drafts (called on logout)
 */
export async function clearAllDrafts() {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.clear();

        await new Promise((resolve, reject) => {
            tx.oncomplete = resolve;
            tx.onerror = () => reject(tx.error);
        });

        db.close();
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to clear drafts', err);
    }
}

/**
 * Remove expired drafts (older than 24h) — can be called periodically
 */
export async function cleanupExpiredDrafts() {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        const all = await new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });

        const now = Date.now();
        const expired = all.filter((d) => now - d.updatedAt > EXPIRY_MS);
        expired.forEach((d) => store.delete(d.draftId));

        await new Promise((resolve, reject) => {
            tx.oncomplete = resolve;
            tx.onerror = () => reject(tx.error);
        });

        db.close();
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to cleanup expired drafts', err);
    }
}

/**
 * Keep only the newest MAX_DRAFTS per practice — delete oldest beyond cap
 */
async function enforceLimit(practiceId) {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const index = store.index('practiceId');

        const all = await new Promise((resolve, reject) => {
            const request = index.getAll(practiceId);
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });

        if (all.length > MAX_DRAFTS) {
            // Sort newest first, delete the overflow
            all.sort((a, b) => b.updatedAt - a.updatedAt);
            const toDelete = all.slice(MAX_DRAFTS);
            toDelete.forEach((d) => store.delete(d.draftId));
        }

        await new Promise((resolve, reject) => {
            tx.oncomplete = resolve;
            tx.onerror = () => reject(tx.error);
        });

        db.close();
    } catch (err) {
        console.warn('callScriptDraftDB: Failed to enforce limit', err);
    }
}

/**
 * Generate a unique draft ID for a new session
 */
export function generateDraftId(practiceId, scriptCategory) {
    const rand = Math.random().toString(36).slice(2, 8);
    return `${practiceId}_${scriptCategory}_${Date.now()}_${rand}`;
}

/**
 * Format a draft timestamp for display (e.g., "2 hours ago", "Yesterday at 3:45 PM")
 */
export function formatDraftTime(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;

    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
